import React from "react";
import { collection, query, where, getDocs } from "@firebase/firestore";

import { db } from "./firebase";
import SubmissionSuccess from "./SubmissionSuccess";
import { joinWaitlist } from "./services";

const spinner = (
  <div className="text-center w-5 h-5 rounded-full animate-spin border-2 border-solid border-white border-t-transparent my-0 mx-auto"></div>
);

const Widget = () => {
  const [loading, setLoading] = React.useState(true);
  const [waitlistData, setwaitlistData] = React.useState(null);
  const [email, setEmail] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submissionSuccess, setSubmissionSuccess] = React.useState(false);
  const [apiError, setApiError] = React.useState(null);
  const [response, setResponse] = React.useState(null);

  React.useEffect(() => {
    const url = new URL(window.location.href);
    let waitlistId = url.searchParams.get("waitlistId");
    const referralCode = url.searchParams.get("referral_code");

    if (!waitlistId) {
      const container = document.getElementById("prelaunchlist-widget");
      waitlistId = container.getAttribute("data-waitlist-id");
    }

    const getData = async () => {
      const waitlistRef = collection(db, "waitlists");
      const q = query(waitlistRef, where("waitlist_key", "==", waitlistId));

      try {
        const docs = await getDocs(q);
        if (!docs.empty) {
          let data = {};
          docs.forEach((d) => {
            data = d.data();
          });
          setwaitlistData({
            ...data,
            docId: data.id,
          });
        }
      } catch (e) {
        console.error("Error getting documents: ", e);
      }
      setLoading(false);
    };

    if (!waitlistId) {
      setLoading(false);
    } else {
      getData();
    }
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await joinWaitlist({
        email,
        waitlistId: waitlistData.id
      });

      if (response.error) {
        setApiError(response.error);
      } else {
        setResponse(response);
        setSubmissionSuccess(true);
      }
    } catch (e) {
      console.error("Error adding document: ", e);
    }
    setIsSubmitting(false);
  };

  const isEmailValid = React.useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, [email]);

  if (loading) {
    return (
      <div className="min-w-96 rounded-lg shadow-md p-4 animate-pulse">
        <div className="w-2/3 h-4 bg-gray-300 rounded m-auto mb-2"></div>
        <div className="w-full h-8 bg-gray-300 rounded mb-2"></div>
        <div className="w-2/3 h-8 bg-gray-300 m-auto rounded"></div>
      </div>
    );
  }

  if (!waitlistData) {
    return <div>Widget not found</div>;
  }

  if (submissionSuccess) {
    return (
      <SubmissionSuccess waitlistData={waitlistData} response={response} />
    );
  }

  const isButtonDisabled = !email || !isEmailValid || isSubmitting;

  const waitlistConfig = waitlistData.widget_config;

  const isFormVerticalStacked =
    waitlistConfig.fieldStackPosition === "vertical";

  const showError = (!isEmailValid && email) || apiError;

  return (
    <div className="p-5">
      <div className="font-bold text-center text-3xl">
        {waitlistConfig.title}
      </div>
      <div className="text-center my-5">{waitlistConfig.subTitle}</div>
      <div
        className={`flex ${
          isFormVerticalStacked ? "flex-col mt-3" : "flex-row gap-3"
        }`}
      >
        <div className={`flex-1 flex flex-col`}>
          <input
            type="email"
            placeholder={waitlistConfig?.placeholderText || "Enter your email"}
            className="border p-2 rounded-md w-full mb-3"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setApiError((prev) => {
                if (prev) return null;
              });
            }}
            style={{
              borderRadius:
                waitlistConfig?.inputBorderRadius?.value || "0.375rem",
            }}
          />
          {showError && (
            <div className="mb-3 text-red-500 text-sm">
              {!isEmailValid && email && "Please enter a valid email"}
              {apiError}
            </div>
          )}
        </div>
        <button
          type="button"
          style={{
            height: "42px",
            paddingLeft: "12px",
            paddingRight: "12px",
            background: waitlistConfig?.buttonColor || "#475569",
            color: waitlistConfig?.buttonTextColor || "#ffffff",
            border: `1px solid ${waitlistConfig?.buttonBorderColor}`,
            borderRadius:
              waitlistConfig?.buttonBorderRadius?.value || "0.375rem",
            fontWeight: "400",
          }}
          className={`text-white font-medium rounded-lg px-4 py-1.5 ${
            isButtonDisabled && "cursor-not-allowed"
          }`}
          disabled={isButtonDisabled}
          onClick={handleSubmit}
        >
          {isSubmitting ? spinner : waitlistConfig?.ctaText || "Join waitlist"}
        </button>
      </div>
    </div>
  );
};

export default Widget;
